import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import styled, { css } from "styled-components";

import Subscription from "../../store/subscription/model";
import CartProduct from "../../store/cart-product/model";
import { Icons } from "../../utils/react-svg";
import i18nSelectors from "../../store/i18n/selectors";
import planSelectors from "../../store/plan/selectors";
import productSelectors from "../../store/product/selectors";
import SubscriptionItem from "../../store/subscription-item/model";
import Cart from "../../store/cart/model";
import subscriptionItemSelectors from "../../store/subscription-item/selectors";
import InfoTooltip from "../InfoTooltip";
import { Color } from "../../utils/styleDesignSystem";

import { navigate } from "../../services/navigation";

import { getContentfulProductForPlan } from "../../utils/planToProduct";
import { estimateShippingAndDeliveryDates } from "../confirmation/utils";
import {
  addCartToSubscription,
  fetchSubscriptionInvoicePreview,
} from "./utils";
import locales, { Locale } from "../../../config/locales";
import { centsToCurrencyString } from "../../utils/centsToCurrencyString";
import PulsingEllipsis from "../global/PulsingEllipsis";
import metrics from "../../utils/metrics";

interface Props {
  subscriptions: Subscription[];
  cartProducts: CartProduct[];
  onClose: () => void;
  cart: Cart;
}

const Container = styled.div`
  height: 100%;
  width: 100%;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;

  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--spacing-1) var(--spacing-1_5);
  padding-bottom: 116px;
`;

const BackButtonContainer = styled.div`
  width: 100%;
  &:hover > * {
    color: var(--indigo-blue-60) !important;
  }

  &:hover {
    svg {
      path {
        stroke: var(--indigo-blue-60) !important;
      }
    }
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  width: fit-content;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 0;
  margin-top: var(--spacing-0_5);
  color: var(--indigo-blue);

  svg {
    margin-right: var(--spacing-0_5);
    height: 24px;
  }
`;

const Header = styled.h2`
  text-align: left;
  font-size: 32px;
  font-weight: 450;
  margin-top: var(--spacing-1_5);
  width: 100%;
`;

const Subheader = styled.p`
  width: 100%;
  text-align: left;
  margin-top: var(--spacing-1);
  margin-bottom: 0;
`;

const Bar = styled.hr`
  margin-top: var(--spacing-1_5);
  margin-bottom: var(--spacing-1_5);
  border-top: 2px solid var(--indigo-blue, #142b6f);
  width: 100%;
`;

const LightBar = styled.hr`
  border-top: 1px solid var(--indigo-blue-20);
  margin-bottom: var(--spacing-1);
  width: 100%;
`;

const Option = styled.div<{ $selected?: boolean }>`
  border-radius: var(--spacing-0_25, 4px);
  border: 1px solid var(--indigo-blue-20, #e0e5f2);

  border-color: ${(props) =>
    props.$selected ? `var(--indigo-blue)` : `var(--indigo-blue-20)`};
  margin-bottom: var(--spacing-1);
  background: var(--neutral-white, #fff);
  padding: var(--spacing-1);

  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const OptionTop = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  min-height: 50px;
`;

const SelectedIndicatorContainer = styled.div`
  width: 20px;
  height: 100%;
  margin-right: var(--spacing-1);
  padding-top: 3px;
`;
const SelectedIndicator = styled.div<{ $selected?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border-style: solid;
  border-color: ${(props) =>
    props.$selected ? `var(--indigo-blue)` : `var(--indigo-blue-20)`};
  border-width: ${(props) => (props.$selected ? `4px` : `1px`)};
  position: relative;
  transition: all 150ms ease-in;

  &:hover {
    border-color: ${(props) => !props.$selected && `var(--indigo-blue-40)`};
  }

  &::after {
    position: absolute;
    display: block;
    content: " ";
    height: 0;
    width: 0;
    top: 50%;
    left: 50%;
    background-color: var(--indigo-blue);
    border-radius: 50%;
    transition: all 150ms ease-in;
  }

  ${(props) =>
    props.$selected &&
    `
    border-color: var(--indigo-blue);
    background-color: var(--indigo-blue);
    animation: fadeInBorder 300ms ease-in-out;
  `}

  ${(props) =>
    props.$selected &&
    `
    &::after {
      background-color: var(--white);
      width: 9px;
      height: 9px;
      top: calc(50% - 9px / 2);
      left: calc(50% - 9px / 2);
    }
  `}

  @keyframes fadeInBorder {
    from {
      border-color: var(--indigo-blue-20);
    }
    to {
      border-color: var(--indigo-blue);
    }
  }
`;

const OptionName = styled.h4<{ $selected?: boolean }>`
  color: var(--indigo-blue);
  font-weight: ${(props) =>
    props.$selected ? `500 !important` : `450 !important`};
`;

const OptionSubheader = styled.p`
  color: var(--indigo-blue-60);
  margin-top: 2px;
  margin-bottom: 0;
`;

const OptionBody = styled.div`
  width: 100%;
`;

const ExistingSubscriptionProducts = styled.ul`
  list-style: none;
  margin-bottom: 0;
  :last-child {
    margin-bottom: 0;
  }
`;

const ExistingSubscriptionProductName = styled.li`
  margin-bottom: var(--spacing-0_5);
  color: var(--indigo-blue-60);
  svg {
    path {
      stroke: var(--indigo-blue-60) !important;
    }

    margin-right: var(--spacing-0_5);
  }
`;

const CTAContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
`;

const CTAContainerBottom = styled.div`
  border-top: 1px solid var(--indigo-blue-20);
  padding-left: var(--spacing-1_5);
  padding-right: var(--spacing-1_5);
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: fixed;
  bottom: 0;
  z-index: 2;
  background: var(--neutral-white, #fff);
  left: 0;
  right: 0;
  margin: 0 auto;
  max-width: 100%;
`;

const NextShipmentTotal = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: var(--spacing-1);
`;

const NextShipmentTotalText = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-0_5);
`;

const ContinueButton = styled.button<{ $processing?: boolean }>`
  display: flex;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  border-radius: 25px;
  border: none;
  background: var(--indigo-blue);
  color: var(--white);
  height: 48px;
  transition:
    background 0.2s,
    color 0.2s;
  font-size: 16px;
  line-height: 24px;
  margin-top: var(--spacing-1);
  margin-bottom: var(--spacing-1);
  width: 100%;

  ${({ $processing }) =>
    $processing &&
    css`
      background: var(--indigo-blue-20);
    `}

  &:hover {
    background: var(--yellow-ochre);
    color: var(--indigo-blue);
  }
`;

const ButtonCopy = styled.span.attrs({ className: "typography-label1" })`
  font-size: var(--spacing-1, 16px) !important;
`;

const SubscriptionSelectButton = ({
  subscription,
  selected,
  select,
  locale,
  subscriptions,
}: {
  subscription: Subscription;
  selected: boolean;
  select: React.Dispatch<React.SetStateAction<string>>;
  locale: Locale;

  subscriptions: Subscription[];
}) => {
  const items: SubscriptionItem[] = useSelector((state) =>
    subscriptionItemSelectors.allItemsForSubscription(state, subscription),
  );
  const productNames = useSelector((state) =>
    items.map((item) => {
      const contentfulProduct = getContentfulProductForPlan(item.planId);
      const plan = planSelectors.planForId(state, {
        id: item.planId,
      });

      const product = productSelectors.productForId(state, {
        id: plan.productId,
      });

      return contentfulProduct?.name.name || product?.name;
    }),
  );

  const deliveryEstimate = estimateShippingAndDeliveryDates(
    locale,
    new Date(subscription.upcomingInvoiceAt),

    "MMMM D",
  ).deliveryDate;

  const subscriptionName =
    subscriptions.length > 1
      ? `Ritual ${subscription.ritualName}`
      : "Your Subscription";

  return (
    <Option
      onClick={() => select(subscription.id)}
      $selected={selected}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          select(subscription.id);
        }
      }}
      data-testid={`subscription-select-button-${subscription.id}`}
    >
      <OptionTop>
        <div>
          <SelectedIndicatorContainer>
            <SelectedIndicator $selected={selected} />
          </SelectedIndicatorContainer>
        </div>
        <OptionBody>
          <OptionName
            className="typography-body1 font-circular"
            $selected={selected}
          >
            Add to {subscriptionName}
          </OptionName>
          <OptionSubheader className="typography-body3 font-circular">
            Estimated delivery by {deliveryEstimate}
          </OptionSubheader>
        </OptionBody>
      </OptionTop>
      <OptionBody>
        <LightBar />
        <ExistingSubscriptionProducts>
          {productNames.map((name, index) => (
            <ExistingSubscriptionProductName
              key={index}
              className="typography-body3 font-circular"
            >
              <Icons.Check16x16 />
              {name}
            </ExistingSubscriptionProductName>
          ))}
        </ExistingSubscriptionProducts>
      </OptionBody>
    </Option>
  );
};

const AddToSubscriptionInterstitial = ({
  subscriptions,
  cartProducts,
  onClose,
  cart,
}: Props) => {
  const [selectedOption, setSelectedOption] = useState<string>("checkout");
  const [processing, setProcessing] = useState<boolean>(false);
  const [invoiceTotal, setInvoiceTotal] = useState<{ [key: string]: string }>(
    {},
  );

  const localeId = useSelector(i18nSelectors.locale);
  const locale = locales[localeId]!;

  const selectedSubscription = subscriptions.find(
    (subscription) => subscription.id === selectedOption,
  );

  const asapDeliveryEstimate = estimateShippingAndDeliveryDates(
    locale,
    new Date(),
    "MMMM D",
  ).deliveryDate;

  useEffect(() => {
    const fetchAllPreviews = async (): Promise<void> => {
      const results = await Promise.all(
        subscriptions.map(async (subscription) => {
          const { total } = await fetchSubscriptionInvoicePreview(
            cartProducts,
            subscription,
          );
          return { [subscription.id]: centsToCurrencyString(total) };
        }),
      );
      setInvoiceTotal(Object.assign({}, ...results));
    };
    fetchAllPreviews();
  }, [cartProducts, subscriptions]);

  useEffect(() => {
    metrics.track("Add to Subscription Interstitial Viewed", {
      cart_id: cart.id,
      subscriptions: subscriptions,
    });
  }, [cart, subscriptions]);

  const nextShipmentTotal = selectedSubscription
    ? invoiceTotal[selectedSubscription.id]
    : centsToCurrencyString(cart.total);

  useEffect(() => {
    const title =
      selectedOption === "checkout"
        ? "Get it ASAP"
        : `Add to Ritual ${selectedSubscription?.ritualName}`;

    metrics.track("Shipping Option Selected", {
      title,
      next_shipment_total: nextShipmentTotal,
      subscription: selectedSubscription,
    });
  }, [selectedOption, selectedSubscription, nextShipmentTotal]);

  const handleCTAClick = async () => {
    if (selectedOption === "checkout") {
      metrics.track("Shipping Modal CTA Clicked", {
        title: "Continue to Checkout",
        next_shipment_total: nextShipmentTotal,
      });
      navigate("/checkout");
    } else {
      metrics.track("Shipping Modal CTA Clicked", {
        title: "Add to Subscription",
        next_shipment_total: nextShipmentTotal,
      });

      metrics.track("Products Added to Subscription", {
        cart_id: cart.id,
        cart_products: cartProducts,
        location: "flyout_cart",
      });
      setProcessing(true);
      await addCartToSubscription(selectedOption);
      navigate(process.env.GATSBY_ACCOUNT_URL || "https://account.ritual.com");
    }
  };

  const handleBackButtonClick = () => {
    metrics.track("Select Shipping Modal Back Pressed");
    onClose();
  };

  const buttonText = () => {
    if (processing) {
      return (
        <span>
          Processing <PulsingEllipsis />
        </span>
      );
    }
    return selectedOption === "checkout"
      ? "Continue to Checkout"
      : "Add to Subscription";
  };

  const tooltipContent = () => {
    if (selectedOption === "checkout") return;
    if (subscriptions.length > 1) {
      return `Your next shipment total includes your current Ritual ${selectedSubscription?.ritualName} subscription, along with your new items.`;
    }
    return "Your next shipment total includes your current subscription, along with your new items.";
  };

  return (
    <Container>
      <BackButtonContainer>
        <BackButton
          onClick={handleBackButtonClick}
          className="typography-label4 font-circular"
          data-testid="add-to-sub-back-button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              onClose();
            }
          }}
        >
          <Icons.ArrowLeftShort />
          Back to Cart
        </BackButton>
      </BackButtonContainer>
      <Header className="typography-heading2 font-circular">
        Select Shipping Method
      </Header>
      <Subheader className="typography-body3 font-dutch">
        Your new item can ship with an upcoming order. Please select an option
        below.
      </Subheader>
      <Bar />

      <Option
        onClick={() => setSelectedOption("checkout")}
        $selected={selectedOption === "checkout"}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            setSelectedOption("checkout");
          }
        }}
      >
        <OptionTop>
          <div>
            <SelectedIndicatorContainer>
              <SelectedIndicator $selected={selectedOption === "checkout"} />
            </SelectedIndicatorContainer>
          </div>
          <OptionBody>
            <OptionName
              className="typography-body1 font-circular"
              $selected={selectedOption === "checkout"}
            >
              Get it ASAP
            </OptionName>
            <OptionSubheader className="typography-body3 font-circular">
              Estimated delivery by {asapDeliveryEstimate}
            </OptionSubheader>
          </OptionBody>
        </OptionTop>
        <OptionBody>
          <LightBar />
          <OptionSubheader className="typography-body3 font-circular">
            Items will ship separately from any existing subscriptions.
          </OptionSubheader>
        </OptionBody>
      </Option>

      {subscriptions
        .sort(
          (a, b) =>
            new Date(a.upcomingInvoiceAt).getTime() -
            new Date(b.upcomingInvoiceAt).getTime(),
        )
        .map((subscription) => (
          <SubscriptionSelectButton
            key={subscription.id}
            subscription={subscription}
            selected={selectedOption === subscription.id}
            select={setSelectedOption}
            locale={locale}
            subscriptions={subscriptions}
          />
        ))}
      <CTAContainer>
        <CTAContainerBottom>
          <NextShipmentTotal className="typography-label3 font-circular">
            <NextShipmentTotalText>
              Next Shipment's Total
              {selectedSubscription && (
                <InfoTooltip
                  color={Color.indigoBlue60}
                  hoverColor={Color.indigoBlue}
                  content={
                    <span className="typography-caption font-circular">
                      {tooltipContent()}
                    </span>
                  }
                  placement="top"
                  additionalProps={{
                    offset: [-12, 12],
                    $width: "360px",
                    $maxWidth: "360px",
                  }}
                />
              )}
            </NextShipmentTotalText>
            <span>{nextShipmentTotal}</span>
          </NextShipmentTotal>

          <ContinueButton
            className="fullwidth"
            onClick={handleCTAClick}
            aria-label="Continue"
            disabled={!selectedOption}
            aria-disabled={!selectedOption}
            $processing={processing}
          >
            <ButtonCopy>{buttonText()}</ButtonCopy>
          </ContinueButton>
        </CTAContainerBottom>
      </CTAContainer>
    </Container>
  );
};

export default AddToSubscriptionInterstitial;
